import { Navigate, useLocation, useParams } from 'react-router-dom';

import usePermissions from '../api/usePermissions';
import { useUserDetails } from '../api/useUserDetails';
import { EPublisherSolutionModel, EPublisherSolutionModelSubType } from '../common/contracts';
import { useLogin } from '../hooks/useLogin';

import { checkRouteAccess } from './routePermissionsUtil';

type PrivateRouteProps = {
  children: JSX.Element;
};

const getRedirectPath = (userDetails: any): string => {
  const projects = userDetails?.projects || [];

  if (projects.length === 1) {
    const [project] = projects;
    const isCheckoutPaymentLink =
      project.projectType === EPublisherSolutionModel.CHECKOUT &&
      project.projectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS;

    if (isCheckoutPaymentLink) return '/settings';
  }

  return '/welcome';
};

function PrivateRoute({ children }: PrivateRouteProps) {
  const location = useLocation();
  const { publisherId, portalId } = useParams();
  const { isLoggedIn } = useLogin();
  const { getPermissions } = usePermissions();
  const { userDetails, isLoading } = useUserDetails();

  if (isLoggedIn() && isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isLoggedIn()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const permissions = getPermissions();
  const currentProjectId = publisherId || portalId;
  const hasAccess = checkRouteAccess(location.pathname, permissions, currentProjectId);

  if (!hasAccess) {
    const redirectPath = getRedirectPath(userDetails);
    return (
      <Navigate
        to={redirectPath}
        replace
        state={{ from: location.pathname, redirectReason: 'no_access' }}
      />
    );
  }

  return children;
}

export default PrivateRoute;
